import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const sizes: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Optional element rendered beside the title (e.g. a badge). */
  titleAside?: ReactNode;
  icon?: ReactNode;
  size?: ModalSize;
  footer?: ReactNode;
  children: ReactNode;
  /** Prevent closing via overlay click / Escape (e.g. while submitting). */
  dismissible?: boolean;
  /** Element to focus first; defaults to the first focusable element in the body. */
  initialFocusRef?: React.RefObject<HTMLElement>;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let openModalCount = 0;

/**
 * Accessible dialog: portal-rendered, focus-trapped, closes on Escape and
 * overlay click, locks body scroll and restores focus to the trigger.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  titleAside,
  icon,
  size = 'md',
  footer,
  children,
  dismissible = true,
  initialFocusRef,
}: ModalProps) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    openModalCount += 1;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    // Forms focus their first field; read-only dialogs focus the panel itself.
    const focusTarget =
      initialFocusRef?.current ??
      panel?.querySelector<HTMLElement>('[data-autofocus]') ??
      panel
        ?.querySelector('[data-modal-body]')
        ?.querySelector<HTMLElement>('input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])') ??
      panel;
    focusTarget?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (!panel) return;
      // Only the top-most modal handles keys.
      const modals = document.querySelectorAll('[data-modal-panel]');
      if (modals[modals.length - 1] !== panel) return;

      if (e.key === 'Escape') {
        if (dismissibleRef.current) {
          e.stopPropagation();
          onCloseRef.current();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      openModalCount -= 1;
      if (openModalCount === 0) document.body.style.overflow = '';
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-modal flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div
        aria-hidden="true"
        className="absolute inset-0 animate-fade-in bg-ink/40 backdrop-blur-[1px]"
        onMouseDown={() => dismissible && onClose()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        data-modal-panel
        className={cn(
          'relative flex max-h-[92vh] w-full animate-scale-in flex-col rounded-t-xl bg-surface shadow-overlay focus:outline-none sm:rounded-xl',
          sizes[size],
        )}
      >
        <header className="flex items-start gap-3 border-b border-line px-6 py-4">
          {icon && (
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={titleId} className="text-base font-semibold text-ink">
                {title}
              </h2>
              {titleAside}
            </div>
            {description && (
              <p id={descId} className="mt-0.5 text-[13px] text-ink-secondary">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!dismissible}
            aria-label="Close dialog"
            className="-mr-2 -mt-1 flex h-8 w-8 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-40"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div data-modal-body className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-muted px-6 py-3.5 sm:rounded-b-xl">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
